import { IconNameEnum, useModuleBEM } from "@b2bportal/core-themes";
import { ProductType } from "@b2bportal/core-types";
import type { HomesReservation } from "@b2bportal/homes-api";
import { useI18nContext } from "@hopper-b2b/i18n";
import { Icon } from "@tenant/ui";
import styles from "./HomesReservationPage.module.scss";
import { useHomesReservationDetails } from "./useHomesReservationDetails";
import { ManageBooking, TripHeader } from "@/components";
import { TripsReservationStatusPill } from "@/components";
import { MyTripPageHeader } from "@/overrides/apac/apac-ui/components/MyTripPageHeader/MyTripPageHeader";

const isValidPhone = (phone: string) => /^[+\d\s()-]+$/.test(phone);
const isValidEmail = (email: string) =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

export const HomesReservationDetail = ({
  reservation,
  onQueryCancellation,
}: {
  reservation: HomesReservation;
  onQueryCancellation: () => void;
}) => {
  const [, cn] = useModuleBEM(styles, "HomesReservationPage");
  const { t } = useI18nContext();
  const data = useHomesReservationDetails(reservation);
  const isActiveBooking = reservation.status.ReservationStatus !== "Cancelled";

  return (
    <>
      <MyTripPageHeader className={cn("page-header")} />

      <div className={cn("card-wrapper")}>
        <TripHeader
          statusPill={
            <TripsReservationStatusPill
              variant={ProductType.HOMES}
              status={reservation.status.ReservationStatus}
            />
          }
          title={t("homesPostBooking.yourStayIn", {
            location: data.location,
          })}
          confirmationCode={data.confirmationCode}
          confirmationLabel={t("hotelTrips.confirmationCode")}
        />

        <div className={cn("two-column")}>
          {/* Summary Card */}
          <div className={cn("summary-card")}>
            {data.image && (
              <div className={cn("image")}>
                <img
                  src={data.image}
                  alt={data.name}
                  onError={(e) => {
                    e.currentTarget.style.display = "none";
                  }}
                />
              </div>
            )}
            <div className={cn("details")}>
              <span className={cn("location-label")}>
                <Icon iconName={IconNameEnum.mapPin} />
                {data.location}
              </span>
              <h2 className={cn("property-name")}>{data.name}</h2>
              {data.address && (
                <div className={cn("address")}>{data.address}</div>
              )}

              <div className={cn("dates")}>
                <div className={cn("date-group")}>
                  <span className={cn("date-label")}>
                    {t("core-hotels.postBooking.reservation.checkInLabel")}
                  </span>
                  <span className={cn("date-value")}>{data.checkInDate}</span>
                </div>
                <div className={cn("date-group")}>
                  <span className={cn("date-label")}>
                    {t("core-hotels.postBooking.reservation.checkOutLabel")}
                  </span>
                  <span className={cn("date-value")}>{data.checkOutDate}</span>
                </div>
              </div>

              {data.guestSummary && (
                <div className={cn("guest-info")}>
                  <span className={cn("guest-label")}>
                    {t(
                      "core-hotels.postBooking.reservation.reservationDetails.numberOfGuestsTitle"
                    )}
                  </span>
                  <span className={cn("guest-value")}>{data.guestSummary}</span>
                </div>
              )}
            </div>
          </div>

          <ManageBooking
            className={cn("manage-booking")}
            variant={ProductType.HOMES}
            itinerary={reservation}
            isActiveBooking={isActiveBooking}
            onQueryCancellation={onQueryCancellation}
          />

          {/* Booking Details */}
          <div className={cn("booking-details")}>
            <h3 className={cn("section-title")}>{t("bookingDetails")}</h3>

            <div className={cn("field")}>
              <span className={cn("field-label")}>
                {t("tripSearch.confirmationCode")}
              </span>
              <span className={cn("field-value")}>{data.confirmationCode}</span>
            </div>

            <div className={cn("field")}>
              <span className={cn("field-label")}>
                {t("hotelTrips.checkingIn")}
              </span>
              <span className={cn("field-value")}>{data.primaryGuestName}</span>
            </div>

            {data.guestSummary && (
              <div className={cn("field")}>
                <span className={cn("field-label")}>{t("guests")}</span>
                <span className={cn("field-value")}>{data.guestSummary}</span>
              </div>
            )}

            <div className={cn("field")}>
              <span className={cn("field-label")}>
                {t("homesPostBooking.lengthOfStay", {
                  defaultValue: "Length of stay",
                })}
              </span>
              <span className={cn("field-value")}>
                {t("homesPostBooking.nights", {
                  defaultValue: "{{count}} night(s)",
                  count: data.lengthOfStay,
                })}
              </span>
            </div>
          </div>

          {/* Cancellation Policy */}
          <div className={cn("cancellation-policy")}>
            <h3 className={cn("section-title")}>
              {t("cancellationPolicyCopy")}
            </h3>
            <div className={cn("cancellation-content")}>
              {"primaryText" in reservation.cancellationPolicy.policy && (
                <span className={cn("cancellation-primary")}>
                  {reservation.cancellationPolicy.policy.primaryText}
                </span>
              )}
              {"secondaryText" in reservation.cancellationPolicy.policy &&
                reservation.cancellationPolicy.policy.secondaryText && (
                  <span className={cn("cancellation-secondary")}>
                    {reservation.cancellationPolicy.policy.secondaryText}
                  </span>
                )}
            </div>
          </div>

          {/* Property Details */}
          {(data.address || data.hostPhone || data.hostEmail) && (
            <div className={cn("property-details")}>
              <h3 className={cn("section-title")}>
                {t("hotelTrips.propertyDetails")}
              </h3>

              {data.address && (
                <div className={cn("field")}>
                  <span className={cn("field-label")}>
                    {t("hotelTrips.address")}
                  </span>
                  <a
                    className={cn("field-link")}
                    href={
                      data.mapsUrl.startsWith("https://") ? data.mapsUrl : "#"
                    }
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {data.address}
                  </a>
                </div>
              )}

              {(data.hostPhone || data.hostEmail) && (
                <div className={cn("field")}>
                  <span className={cn("field-label")}>
                    {t("homesPostBooking.contactHost", {
                      defaultValue: "Contact host",
                    })}
                  </span>
                  {data.hostPhone && isValidPhone(data.hostPhone) && (
                    <a
                      className={cn("field-link")}
                      href={`tel:${data.hostPhone}`}
                    >
                      {data.hostPhone}
                    </a>
                  )}
                  {data.hostEmail && isValidEmail(data.hostEmail) && (
                    <a
                      className={cn("field-link")}
                      href={`mailto:${data.hostEmail}`}
                    >
                      {data.hostEmail}
                    </a>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Checkout Breakdown */}
          <div className={cn("checkout-breakdown")}>
            <h3 className={cn("section-title")}>{t("checkoutBreakdown")}</h3>

            <div className={cn("divider")} />

            <div className={cn("line-items")}>
              <div className={cn("line")}>
                <span>
                  {t("homesPostBooking.nights", {
                    defaultValue: "{{count}} night(s)",
                    count: data.lengthOfStay,
                  })}
                </span>
                <span>{data.sellPrice}</span>
              </div>
              {data.lineItems.map((item, index) => (
                <div key={`${item.label}-${index}`} className={cn("line")}>
                  <span>{item.label}</span>
                  <span>{item.value}</span>
                </div>
              ))}
              {data.totalTaxesAndFees && (
                <div className={cn("line")}>
                  <span>{t("taxesAndFees")}</span>
                  <span>{data.totalTaxesAndFees}</span>
                </div>
              )}
            </div>

            <div className={cn("divider")} />

            <div className={cn("line", ["total"])}>
              <span>{t("total")}</span>
              <span>{data.total}</span>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};
